import { SchemaConfig } from "@/lib/form-v2";
import { ContractType } from "@/lib/api/otc";
import { z } from "zod";
import {
  FormBaseShape,
  FormBasePriceReownValidation,
  FormRangeValidation,
  FormRangePriceReownValidation,
  FormRangeWDUValidation,
  FormReOwnWDUValidation,
  FormMinMaxValidation,
  FormHedgePlusValidation,
  FormPremiumValidation,
  FormDoubleUpValidation,
  FormMinPriceValidation,
  FormMinimumPriceValidation,
  FormAccumulatorValidation,
  FormMinMaxDoubleUpValidation,
} from "./form-schemas";
// Import the raw API variant ZodObjects for shape introspection
import {
  BasePriceReownValidation,
  RangeValidation,
  RangePriceReownValidation,
  RangeWDUValidation,
  ReOwnWDUValidation,
  MinMaxValidation,
  HedgePlusValidation,
  PremiumValidation,
  DoubleUpValidation,
  MinPriceValidation,
  MinimumPriceValidation,
  AccumulatorValidation,
  MinMaxDoubleUpValidation,
} from "@/lib/api/otc";


/**
 * Build the ZodObject variant shapes (for introspection) by extending
 * the form base with each variant's extra fields.
 *
 * These are pure ZodObjects (no superRefine) so the form library can
 * inspect .shape for field names, required status, widget inference.
 */
function variantShape(
  apiVariant: z.ZodObject<z.ZodRawShape>,
): z.ZodObject<z.ZodRawShape> {
  // The form base already has all the extra fields.
  // We just need to merge the variant-specific fields on top.
  // Since apiVariant extends BaseContractValidation, and FormBaseShape
  // extends BaseContractValidation too, we merge their shapes.
  return FormBaseShape.extend(apiVariant.shape) as z.ZodObject<z.ZodRawShape>;
}

/**
 * SchemaConfig for the OTC contract form.
 *
 * - `variants`: ZodObject shapes for introspection (field names, required, etc.)
 * - `validators`: ZodEffects with .superRefine() for actual validation
 * - `base`: FormBaseShape for auto-clearing logic
 */
export const otcSchemaConfig: SchemaConfig = {
  discriminatorField: "contract_type",
  base: FormBaseShape as z.ZodObject<z.ZodRawShape>,
  defaultVariantKey: ContractType.Range,

  // ZodObject shapes for introspection
  variants: {
    [ContractType.BasePriceReown]: variantShape(BasePriceReownValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.Range]: variantShape(RangeValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.RangePriceReown]: variantShape(RangePriceReownValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.RangeWDU]: variantShape(RangeWDUValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.ReOwnWDU]: variantShape(ReOwnWDUValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.MinMax]: variantShape(MinMaxValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.HedgePlus]: variantShape(HedgePlusValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.PremiumOfferWDU]: variantShape(PremiumValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.DoubleUp]: variantShape(DoubleUpValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.MinPriceCallSpread]: variantShape(MinPriceValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.MinimumPrice]: variantShape(MinimumPriceValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.AccWeeklyDU]: variantShape(AccumulatorValidation as z.ZodObject<z.ZodRawShape>),
    [ContractType.MinMaxWDU]: variantShape(MinMaxDoubleUpValidation as z.ZodObject<z.ZodRawShape>),
  },

  // ZodEffects validators (with superRefine for canola, etc.)
  validators: {
    [ContractType.BasePriceReown]: FormBasePriceReownValidation,
    [ContractType.Range]: FormRangeValidation,
    [ContractType.RangePriceReown]: FormRangePriceReownValidation,
    [ContractType.RangeWDU]: FormRangeWDUValidation,
    [ContractType.ReOwnWDU]: FormReOwnWDUValidation,
    [ContractType.MinMax]: FormMinMaxValidation,
    [ContractType.HedgePlus]: FormHedgePlusValidation,
    [ContractType.PremiumOfferWDU]: FormPremiumValidation,
    [ContractType.DoubleUp]: FormDoubleUpValidation,
    [ContractType.MinPriceCallSpread]: FormMinPriceValidation,
    [ContractType.MinimumPrice]: FormMinimumPriceValidation,
    [ContractType.AccWeeklyDU]: FormAccumulatorValidation,
    [ContractType.MinMaxWDU]: FormMinMaxDoubleUpValidation,
  },
};
